import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiGet, apiPost } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const data = await apiGet("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const startGoogleLogin = (redirectPath = "/account") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    const data = await apiPost("/auth/login_password", { email, password });
    await checkAuth();
    return data;
  };

  const registerWithPassword = async (email, password, name) => {
    const data = await apiPost("/auth/register", { email, password, name });
    await checkAuth();
    return data;
  };

  const logout = async () => {
    try {
      await apiPost("/auth/logout", {});
    } catch (err) {
      console.warn("Logout API call failed (clearing local session anyway):", err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        checkAuth,
        startGoogleLogin,
        loginWithPassword,
        registerWithPassword,
        logout,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
